<template>
  <!-- ticker/calendar/news visibility is driven by the slotWidth prop
       (see script) - a stable, independent measurement of ToolBar's slot,
       not this component's own rendered width. -->
  <div class="hm-bar">
    <!-- TICKERS CAROUSEL -->
    <div class="ticker-wrapper" :class="{ 'ticker-wrapper--compact': isCompact }">
      <div ref="ticker" class="ticker-track" :style="{ animationDuration: `${animationDuration}s` }">
        <div class="ticker-item" v-for="([ticker, tickerValue], index) in duplicatedFxMarketIntradayList"
          :key="`${ticker}-${index}`">
          <carousel-ticker :tickersData="tickerValue" :ticker="ticker" :style="{ minWidth: isCompact ? '93px' : '110px' }" />
        </div>
      </div>
    </div>


    <template v-if="showCalendar">
      <div class="hm-sep" />
      <div class="hm-calendar">
        <carousel-economic-calendar />
      </div>
    </template>

    <template v-if="showNews">
      <div class="hm-sep" />
      <div class="hm-news">
        <carousel-news />
      </div>
    </template>
  </div>
</template>

<script>
import { useFxMarketStore } from '@/store/toolbarStore';
import { mapStores } from 'pinia';
import CarouselTicker from './toolbarCarousel/CarouselTicker.vue';
import CarouselEconomicCalendar from './toolbarCarousel/CarouselEconomicCalendar.vue';
import CarouselNews from './toolbarCarousel/CarouselNews.vue';

const NEWS_MAX_SLOT_WIDTH = 350
const CALENDAR_MAX_SLOT_WIDTH = 480

export default {
  components: {
    CarouselTicker,
    CarouselEconomicCalendar,
    CarouselNews
  },
  props: {
    // Real rendered width (px) of the ToolBar slot content; 0 when there's
    // no slot at all (in which case everything shows, same as a small slot).
    slotWidth: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      animationDuration: 20,
      rates: [
        { pair: 'USD/BRL', value: '5.0543' },
        { pair: 'EUR/BRL', value: '5.8635' },
        { pair: 'JPY/BRL', value: '0.0316' },
        { pair: 'EUR/USD', value: '1.1601' },
      ],
    }
  },

  computed: {
    ...mapStores(useFxMarketStore),
    fxMarketIntradayList() {
      if (!this.fxMarketStore.getFxMarketIntradayData) return
      return this.fxMarketStore.getFxMarketIntradayData?.data
    },
    duplicatedFxMarketIntradayList() {
      const entries = Object.entries(this.fxMarketIntradayList || {})
      return entries.concat(entries)
    },

    showNews() {
      return this.slotWidth < NEWS_MAX_SLOT_WIDTH
    },
    showCalendar() {
      return this.slotWidth < CALENDAR_MAX_SLOT_WIDTH
    },
    isCompact() {
      return !this.showNews
    },
  },
}

</script>

<style>
.hm-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  height: 100%;
  width: 100%;
  font-size: 11px;
  color: #fff;
  overflow: hidden;
  flex: 1 1 auto;
  min-width: 0;
}
.hm-calendar,
.hm-news{
  font-size: .9rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}
.hm-rates,
.hm-calendar,
.hm-news {
  min-width: 0;
  overflow: hidden;
  flex: 1 1 auto;
}

/* vertical separator */
.hm-sep {
  width: 1px;
  height: 36px;
  background: rgba(255, 255, 255, 0.12);
  margin: 0 14px;
}

/* --- FX Rates --- */
.hm-rates {
  display: flex;
  gap: 20px;
}

.hm-rate {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.hm-rate__pair {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.06em;
  margin-bottom: 3px;
}

.hm-rate__value {
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* --- Badge --- */
.hm-badge {
  width: 22px;
  height: 25px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
}

/* --- Calendar --- */
.hm-calendar {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.hm-calendar__item {
  display: flex;
  align-items: center;
  gap: 1rem;
  line-height: 1.1;
}

.hm-calendar__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.hm-calendar__name {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.85);
  white-space: nowrap;
}

.hm-calendar__date {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
  margin-left: 4px;
}

/* --- News --- */
.hm-news {
  display: flex;
  min-width: 0;
  flex-direction: column;
  overflow: hidden;
  gap: 3px;
}

.hm-news__time{
  text-align: end;
}
.hm-news__header,
.hm-news__source,
.hm-news__time,
.hm-news__headline {
  min-width: 0;
  overflow: hidden;
  flex: 1 1 auto;
  text-overflow: ellipsis;

}

.hm-news__header {
  display: flex;
  gap: 8px;
  align-items: baseline;
}

.hm-news__source {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.hm-news__time {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
}

.hm-news__headline {
  font-size: 14px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* --- PTAX --- */
.hm-ptax {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.hm-ptax__label {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.hm-ptax__value {
  font-size: 17px;
  font-weight: 500;
  letter-spacing: 0.02em;
}


/** carousle */
.ticker-track {
  display: flex;
  width: max-content;
  min-width: 110px;

  animation: scrollTicker linear infinite;
}

.ticker-track:hover {
  animation-play-state: paused;
}

.ticker-item {
  flex-shrink: 0;
  max-width: 150px;
  margin-right: 8px;
}

.ticker-wrapper {
  overflow: hidden;
  max-width: 550px;
  min-width: 125px;
  margin-top: 15px;
  flex: 1 1 auto;
}

/* ~15% smaller when the toolbar has slot content to make room for it */
.ticker-wrapper--compact {
  max-width: 467px;
  min-width: 106px;
}

.ticker-wrapper--compact .ticker-item {
  max-width: 128px;
}

@keyframes scrollTicker {
  from {
    transform: translateX(0%);
  }

  to {
    transform: translateX(-50%);
  }
}

</style>
